import React from 'react';
import { Users, FileText, Heart, Key, Scale, User, ArrowUpRight } from 'lucide-react';
import { idealClients } from '../mock';
import useReveal from '../hooks/useReveal';

const ICONS = { Users, FileText, Heart, Key, Scale, User };

const ClientCard = ({ client, index }) => {
  const Icon = ICONS[client.icon] || User;
  return (
    <article
      className={`reveal pillar-col group relative py-10 px-4 md:px-8 hover:bg-[color:var(--obsidian-2)]/60 transition-colors duration-500 ${index === 0 ? 'md:pl-0' : ''}`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="w-12 h-12 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)] group-hover:border-[color:var(--green)] group-hover:text-[color:var(--green)] transition-colors">
          <Icon className="w-5 h-5" />
        </div>
        <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/45">{client.number}</div>
      </div>
      <h3 className="font-display text-[22px] md:text-[26px] leading-tight text-[color:var(--parchment)]">
        {client.title}
      </h3>
      <p className="mt-4 text-[color:var(--parchment)]/65 text-[14.5px] leading-relaxed">{client.body}</p>
    </article>
  );
};

const IdealClients = () => {
  const ref = useReveal({ threshold: 0.08 });

  return (
    <section id="clients" ref={ref} className="relative py-16 md:py-20 px-6 md:px-10 border-t border-white/5 overflow-hidden">
      <div className="pointer-events-none absolute -right-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--green)]/[0.07] blur-3xl" />

      <div className="relative max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-[.55fr_.45fr] gap-10 items-end mb-16">
          <div>
            <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
              &mdash; Who We Serve
            </div>
            <h2 className="reveal font-display text-[38px] md:text-[64px] leading-[1.02] text-[color:var(--parchment)]">
              Built for the moments that <span className="text-[color:var(--green)]">matter</span>.
            </h2>
          </div>
          <div className="reveal md:justify-self-end">
            <p className="text-[color:var(--parchment)]/70 leading-relaxed max-w-md">
              Every estate is unique &mdash; and so is every client. From families in transition to real estate and legal professionals, we tailor our services to fit your needs, goals, and circumstances.
            </p>
            <a href="#sell" className="mt-6 btn-outline">
              Start Your Sale <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Cinematic warmth banner */}
        <figure className="reveal relative mb-16 md:mb-20 rounded-2xl overflow-hidden border border-white/10">
          <div className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9]">
            <img
              src="/who-we-serve.webp" srcSet="/who-we-serve-400w.webp 400w, /who-we-serve-800w.webp 800w, /who-we-serve.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
              alt="A sculpted lounge chair and ottoman warmed by firelight in a quiet industrial space"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Caption block */}
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-10">
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div
                  className="max-w-xl rounded-xl px-4 py-3 sm:px-5 sm:py-4 md:px-6 md:py-5"
                  style={{
                    backgroundColor: 'rgba(0,0,0,0.25)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    boxShadow: '0 1px 24px -12px rgba(0,0,0,0.35)',
                  }}
                >
                  <p className="font-display text-[13px] sm:text-[18px] md:text-[26px] leading-[1.2] text-[color:var(--parchment)]">
                    Every fine piece deserves a careful next chapter &mdash; <span className="text-[color:var(--green)]">handled with warmth, respect, and the room to breathe</span>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </figure>

        {/* Two-row pillar grid */}
        <div className="grid md:grid-cols-3 gap-0 border-t border-white/10">
          {idealClients.slice(0, 3).map((c, i) => (
            <ClientCard key={c.number} client={c} index={i} />
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-0 border-t border-white/10">
          {idealClients.slice(3, 6).map((c, i) => (
            <ClientCard key={c.number} client={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default IdealClients;
